import { Component, Mixins, Ref } from "vue-property-decorator";
import { Mutation } from "vuex-class";

import DialogModal from "@/components/DialogModal.vue";
import VModal from "@/components/VModal.vue";
import { TemplateSelect } from "@/components/Agencies/ReportBuilder";
import {
  PendingSelectAll,
  buildCompetitorAttributes,
  emptyPendingSelectAll,
  parametersFromCompetitor,
  pendingSelectAllFromCompetitor,
  validateCompetitorAttributes,
} from "@/components/Agencies/ReportBuilder/templateParameters";
import { ModalAction } from "@/components/modalActionTypes";
import ReportBuilderMixin, {
  ReportBuilderTabKey,
} from "@/mixins/ReportBuilder/ReportBuilderMixin";
import ReportTemplateMixin, {
  CompetitorReportTemplateParameters,
  ReportTemplateParameters,
  TemplateKind,
} from "@/mixins/http/ReportTemplateMixin";
import { ReportTemplate } from "@/models";
import {
  reportNameValidation,
  sanitizeReportName,
} from "@/services/ReportNameHelper";
import ToastInvoker, { responseErrorMessage } from "@/utils/ToastInvoker";

export type ConfirmActionKey = "cancel" | "confirm";

@Component
export default class ReportTemplateCrudMixin extends Mixins(
  ReportBuilderMixin,
  ReportTemplateMixin
) {
  @Mutation("reportTemplatesCache/invalidateTemplatesCache")
  readonly invalidateTemplatesCache!: () => void;
  @Mutation("automaticReportsCache/invalidateAutomaticReportsCache")
  readonly invalidateAutomaticReportsCache!: () => void;

  @Ref("templateSelect") readonly templateSelect?: TemplateSelect;
  @Ref("templateNameModal") readonly templateNameModal?: VModal;
  @Ref("editNameModal") readonly editNameModal?: VModal;
  @Ref("templateActionModal") readonly templateActionModal?: DialogModal;

  templateName = "";
  templateNameDraft = "";
  scheduleTemplateName = "";
  readonly nameValidation = reportNameValidation;

  editingTemplateId: string | null = null;
  pendingTemplateId: string | null = null;
  pendingTemplate: ReportTemplate | null = null;
  pendingTemplateName: string | null = null;
  pendingSelectAll: PendingSelectAll = emptyPendingSelectAll();
  savingTemplate = false;
  loadingTemplate = false;
  deletingTemplates = false;
  creatingTemplate = false;

  readonly templatePeriodNote =
    "Il template discerne dal periodo: sono mostrate tutte le opzioni " +
    "disponibili. Alla generazione di un report, le opzioni non disponibili per " +
    "il periodo scelto verranno filtrate automaticamente.";

  get templateActionsBusy(): boolean {
    return (
      this.loadingTemplate || this.generatingReport || this.deletingTemplates
    );
  }

  get hasPendingTemplate(): boolean {
    return this.pendingTemplateId !== null;
  }

  get isEditingTemplate(): boolean {
    return this.editingTemplateId !== null;
  }

  get editingTemplateLabel(): string {
    return this.templateName || `#${this.editingTemplateId}`;
  }

  get disabledSteps(): string[] {
    return this.isTemplateFlow ? ["period"] : [];
  }

  protected applyTemplateToParameters(template: ReportTemplate) {
    const competitor = template.competitor;
    if (!competitor) return;

    this.pendingSelectAll = pendingSelectAllFromCompetitor(competitor);
    this.parameters = parametersFromCompetitor(competitor, this.parameters);
  }

  deselectTemplate() {
    this.pendingTemplateId = null;
    this.pendingTemplateName = null;
    this.pendingTemplate = null;
  }

  onTemplateUse(template: ReportTemplate) {
    this.pendingTemplateId = template.id;
    this.pendingTemplateName = template.name || `Template #${template.id}`;
    this.pendingTemplate = template;
    this.activeTab = ReportBuilderTabKey.SingleReport;
    this.currentStep = "period";
  }

  protected returnToTemplateSettings(): boolean {
    if (!this.$route.query.newTemplate) return false;
    const query: Record<string, string> = {};
    if (this.profile?.slug) query.profile = this.profile.slug;
    this.$router.push({ name: "SettingsTemplateReport", query });
    return true;
  }

  onCreateFromBuilder() {
    this.exitEditMode();
    this.resetBroadcasterStep();
    this.resetSubjectStep();
    this.resetOutputStep();
    this.creatingTemplate = true;
    this.activeTab = ReportBuilderTabKey.SingleReport;
    this.currentStep = "broadcasters";
  }

  exitEditMode() {
    this.editingTemplateId = null;
    this.templateName = "";
    this.creatingTemplate = false;
    this.pendingSelectAll = emptyPendingSelectAll();
    this.currentStep = "period";
  }

  // The wizard configuration as template attributes, or null with the reason
  // already toasted. Every save path goes through here.
  protected validCompetitorAttributes(): CompetitorReportTemplateParameters | null {
    const attributes = buildCompetitorAttributes(
      this.parameters,
      this.allSelected
    );
    const validationError = validateCompetitorAttributes(attributes);
    if (validationError) {
      ToastInvoker.errorToast(validationError, null);
      return null;
    }
    return attributes;
  }

  openCreateTemplateModal() {
    if (!this.validCompetitorAttributes()) return;
    this.templateName = "";
    this.templateNameModal?.show();
  }

  closeTemplateNameModal() {
    this.templateNameModal?.hide();
  }

  get templateNameModalActions(): ModalAction<ConfirmActionKey>[] {
    return [
      {
        key: "cancel",
        label: "Annulla",
        variant: "secondary",
        disabled: this.savingTemplate,
        run: () => this.closeTemplateNameModal(),
      },
      {
        key: "confirm",
        label: this.savingTemplate ? "Salvataggio…" : "Salva",
        variant: "primary",
        disabled:
          this.savingTemplate ||
          !this.nameValidation(this.templateName).canSave,
        run: () => this.saveTemplate(null),
      },
    ];
  }

  openEditNameModal() {
    this.templateNameDraft = this.templateName;
    this.editNameModal?.show();
  }

  closeEditNameModal() {
    this.editNameModal?.hide();
  }

  confirmEditName() {
    const name = sanitizeReportName(this.templateNameDraft);
    if (!name) return;
    this.templateName = name;
    this.closeEditNameModal();
  }

  get editNameModalActions(): ModalAction<ConfirmActionKey>[] {
    return [
      {
        key: "cancel",
        label: "Annulla",
        variant: "secondary",
        run: () => this.closeEditNameModal(),
      },
      {
        key: "confirm",
        label: "Conferma",
        variant: "primary",
        disabled: !this.nameValidation(this.templateNameDraft).canSave,
        run: () => this.confirmEditName(),
      },
    ];
  }

  // `templateId` null creates, otherwise updates that template in place.
  async saveTemplate(templateId: string | null) {
    if (this.savingTemplate) return;
    const name = sanitizeReportName(this.templateName);
    if (!name) return;
    const competitorAttributes = this.validCompetitorAttributes();
    if (!competitorAttributes) return;

    const payload: ReportTemplateParameters = {
      name,
      kind: TemplateKind.COMPETITOR,
      competitor_attributes: competitorAttributes,
    };
    this.savingTemplate = true;
    try {
      if (templateId === null) {
        await this.createReportTemplate(this.profile.slug, payload);
        this.closeTemplateNameModal();
        this.invalidateTemplatesCache();
        if (!this.creatingTemplate) {
          await this.templateActionModal?.dialog("Template salvato.");
          return;
        }
      } else {
        await this.updateReportTemplate(this.profile.slug, templateId, payload);
        if (templateId === this.pendingTemplateId) {
          this.pendingTemplateName = payload.name;
          const refreshed = await this.showReportTemplate(
            this.profile.slug,
            templateId
          );
          if (refreshed) this.pendingTemplate = refreshed;
        }
        this.invalidateTemplatesCache();
      }
      this.exitEditMode();
      this.reset();
      this.activeTab = ReportBuilderTabKey.ReportTemplate;
      await this.templateActionModal?.dialog(
        templateId === null ? "Template salvato." : "Template aggiornato."
      );
      this.returnToTemplateSettings();
    } catch (error) {
      ToastInvoker.errorToast(responseErrorMessage(error), error);
    } finally {
      this.savingTemplate = false;
    }
  }

  async onTemplateEdit(templateId: string) {
    this.loadingTemplate = true;
    try {
      let template: ReportTemplate | undefined;
      try {
        template = await this.showReportTemplate(this.profile.slug, templateId);
      } catch (error) {
        ToastInvoker.errorToast(responseErrorMessage(error), error);
        return;
      }
      if (!template) return;
      this.restoringTemplate = true;
      this.applyTemplateToParameters(template);
      this.editingTemplateId = templateId;
      this.templateName = template.name ?? "";
      this.activeTab = ReportBuilderTabKey.SingleReport;
      this.currentStep = "broadcasters";
      this.$nextTick(() => {
        this.restoringTemplate = false;
      });
    } finally {
      this.loadingTemplate = false;
    }
  }
}
